import { TOP_BAR_HEIGHT } from "@v2/components/header/constants";

const PLP_HEADER_SCROLL_REQUEST_KEY = 'plp:scroll-to-header';

// Where the page comes to rest after a control changed the list under it.
// 'header' stops at the toolbar, with the first row of products under it, which is
// what a filter or a sort changed. 'categories' stops one block higher, on the
// category strip: picking a category reloads the strip with the level below it, and
// the shopper has to see the choices they just opened, not be carried past them.
export type PlpScrollTarget = 'header' | 'categories';

const TARGET_ELEMENT_IDS: Record<PlpScrollTarget, string> = {
    header: 'plp_header_holder',
    categories: 'plp_cover',
};

// The site header hides itself once the page has scrolled this far down.
const HEADER_HIDE_THRESHOLD = 116;

// How long the target is allowed to keep moving before the scroll starts anyway.
const SETTLE_BUDGET_MS = 300;

// A request belongs to the click that made it. If the toolbar it was meant for never
// remounted — the new address rendered the same header — the request would sit in
// storage and be picked up by the next mount instead, which is usually a back
// navigation landing somewhere else entirely.
const REQUEST_MAX_AGE_MS = 4000;

export function requestPlpHeaderScroll(target: PlpScrollTarget = 'header') {
    try {
        sessionStorage.setItem(PLP_HEADER_SCROLL_REQUEST_KEY, `${Date.now()}:${target}`);
    } catch { /* Storage can be unavailable in restricted browsing modes. */ }
}

export function consumePlpHeaderScrollRequest(): PlpScrollTarget | null {
    try {
        const stored = sessionStorage.getItem(PLP_HEADER_SCROLL_REQUEST_KEY) ?? '';
        sessionStorage.removeItem(PLP_HEADER_SCROLL_REQUEST_KEY);

        const [requestedAtPart, targetPart] = stored.split(':');
        const requestedAt = Number.parseInt(requestedAtPart, 10);
        if (!Number.isFinite(requestedAt) || Date.now() - requestedAt > REQUEST_MAX_AGE_MS) return null;

        return targetPart === 'categories' ? 'categories' : 'header';
    } catch {
        return null;
    }
}

// A resting page can sit anywhere in this band without being moved.
const REST_TOLERANCE = 2;

// The two places the target comes to rest, depending on whether the site header is
// open or has collapsed into its top bar.
function measureRestPositions(target: HTMLElement) {
    const stickyHeader = document.getElementById('plp_header');
    const targetTop = target.getBoundingClientRect().top + window.scrollY;

    // scrollMarginTop carries the height of the open header, which is where the
    // toolbar rests whenever the page is scrolling up or has barely moved.
    const openOffset = Number.parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;

    return {
        openTop: Math.max(0, targetTop - openOffset),
        collapsedTop: Math.max(0, targetTop - (stickyHeader ? TOP_BAR_HEIGHT : openOffset)),
    };
}

// Where the page has to sit for the target to come to rest right under the site
// header, with the rest of the page below it.
function measureTargetScrollY(target: HTMLElement) {
    const { openTop, collapsedTop } = measureRestPositions(target);
    // Scrolling down past the threshold hides the site header and lifts the
    // toolbar to the top bar. Landing on the open position in that case would
    // leave a header-sized gap above the list once the header pulls away.
    const isScrollingDown = openTop > window.scrollY;

    return isScrollingDown && collapsedTop >= HEADER_HIDE_THRESHOLD ? collapsedTop : openTop;
}

// The header answers the scroll itself: any move up pulls it back out, any move
// down folds it away. A page already sitting between the two resting spots has the
// target in view under the header either way, so scrolling it onto the other spot
// would buy nothing and flip the header while doing it - which is what made the
// menu appear and disappear on every other category switch.
function isAtRest(target: HTMLElement) {
    const { openTop, collapsedTop } = measureRestPositions(target);

    return window.scrollY >= openTop - REST_TOLERANCE && window.scrollY <= collapsedTop + REST_TOLERANCE;
}

// Everything above the list — the hero, its images, any landing component pinned
// after it — settles over the frames right after a commit, and the toolbar slides
// down with it. Measuring once on that first frame is what used to land the page
// at the very top of the document, so the target is re-read until it holds still.
export default function scrollToPlpHeader(scrollTarget: PlpScrollTarget = 'header') {
    // A page that draws no category strip — search results, a brand, a header with a
    // cover — still has its toolbar, so the scroll falls back to it.
    const target = document.getElementById(TARGET_ELEMENT_IDS[scrollTarget])
        ?? document.getElementById(TARGET_ELEMENT_IDS.header);
    if (!target) return () => { };

    const deadline = Date.now() + SETTLE_BUDGET_MS;
    let previous: number | null = null;
    let frame = 0;

    const tick = () => {
        const targetScrollY = measureTargetScrollY(target);
        const settled = previous !== null && Math.abs(targetScrollY - previous) < 1;

        if (settled || Date.now() >= deadline) {
            // A hop of a few pixels reads as a twitch, not as a move to the list.
            if (!isAtRest(target) && Math.abs(targetScrollY - window.scrollY) > REST_TOLERANCE) {
                window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
            }
            return;
        }

        previous = targetScrollY;
        frame = window.requestAnimationFrame(tick);
    };

    frame = window.requestAnimationFrame(tick);

    return () => window.cancelAnimationFrame(frame);
}
